import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  Bot, Plus, Play, RotateCcw, Trash2, Sparkles, Loader2, AlertTriangle,
  Mail as MailIcon, Edit3, Clock, History, ChevronRight, CheckCircle2, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, authJsonFetch } from '../lib/api';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const EMPTY_RULE = {
  name: '',
  description: '',
  year: new Date().getFullYear(),
  trigger_match: 'all',
  triggers: [{ kind: 'field_value', field: 'marketing_status', operator: 'equals', value: '' }],
  actions: [{ kind: 'send_email', recipient_type: 'primary_contact', subject: '', body: '', template_id: null, fixed_recipients: [] }],
  enabled: true,
  schedule: 'manual',
  natural_language: '',
};

const TRIGGER_OPERATORS_FIELD = ['equals', 'not_equals', 'is_empty', 'is_not_empty', 'contains'];
const TRIGGER_OPERATORS_DATE = ['days_since_update_gte', 'date_field_is_past', 'date_field_within_days'];

export default function AutomationPage() {
  const [rules, setRules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState({});  // rule_id → bool
  const [nlInput, setNlInput] = useState('');
  const [nlBusy, setNlBusy] = useState(false);
  const [cascadeWarnings, setCascadeWarnings] = useState({ open: false, ruleId: null, warnings: [] });
  const [runDetail, setRunDetail] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, runRes] = await Promise.all([
        authFetch(`${API}/automation/rules`),
        authFetch(`${API}/automation/runs?limit=30`),
      ]);
      if (rRes.ok) setRules((await rRes.json()).rules || []);
      else if (rRes.status === 403) toast.error('Automation Assistant is not enabled — turn it on in Settings → Feature Flags.');
      if (runRes.ok) setRuns((await runRes.json()).runs || []);
    } catch (err) {
      toast.error('Failed to load automation data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => { setEditingRule({ ...EMPTY_RULE }); setEditorOpen(true); };
  const openEdit = (rule) => { setEditingRule(JSON.parse(JSON.stringify(rule))); setEditorOpen(true); };

  const saveRule = async () => {
    if (!editingRule.name?.trim()) { toast.error('Name is required'); return; }
    if (!editingRule.triggers?.length) { toast.error('Add at least one trigger'); return; }
    if (!editingRule.actions?.length) { toast.error('Add at least one action'); return; }
    setSaving(true);
    try {
      const isUpdate = !!editingRule.rule_id;
      const url = isUpdate ? `${API}/automation/rules/${editingRule.rule_id}` : `${API}/automation/rules`;
      const method = isUpdate ? 'PATCH' : 'POST';
      const res = await authJsonFetch(url, { method, body: JSON.stringify(editingRule) });
      if (res.ok) {
        toast.success(isUpdate ? 'Rule updated' : 'Rule created');
        setEditorOpen(false);
        fetchAll();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    const res = await authFetch(`${API}/automation/rules/${rule.rule_id}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Rule deleted'); fetchAll(); }
    else toast.error('Delete failed');
  };

  const toggleEnabled = async (rule) => {
    const res = await authJsonFetch(`${API}/automation/rules/${rule.rule_id}`, {
      method: 'PATCH', body: JSON.stringify({ enabled: !rule.enabled }),
    });
    if (res.ok) { toast.success(rule.enabled ? 'Rule disabled' : 'Rule enabled'); fetchAll(); }
    else toast.error('Update failed');
  };

  const runRule = async (rule, { confirm_cascade = false, dry_run = false } = {}) => {
    setRunning(prev => ({ ...prev, [rule.rule_id]: true }));
    try {
      const res = await authJsonFetch(`${API}/automation/rules/${rule.rule_id}/run-now`, {
        method: 'POST', body: JSON.stringify({ confirm_cascade, dry_run }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Run failed'); return; }
      const data = await res.json();
      if (data.status === 'blocked_loop') {
        setCascadeWarnings({ open: true, ruleId: rule.rule_id, warnings: data.warnings || [] });
        return;
      }
      const s = data.summary || {};
      toast.success(`Run complete — matched ${s.matched}, applied ${s.applied}, queued ${s.queued_email} emails, skipped ${s.skipped}, failed ${s.failed}${dry_run ? ' (dry run)' : ''}`);
      fetchAll();
    } finally {
      setRunning(prev => ({ ...prev, [rule.rule_id]: false }));
    }
  };

  const undoRun = async (run) => {
    if (!window.confirm(`Undo this run? Every applied change will be reversed and queued emails cancelled (if still pending).`)) return;
    const res = await authJsonFetch(`${API}/automation/runs/${run.run_id}/undo`, { method: 'POST' });
    if (res.ok) { toast.success('Run undone — counter-entries logged'); fetchAll(); }
    else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Undo failed'); }
  };

  const parseNL = async () => {
    if (!nlInput.trim()) { toast.error('Describe the rule in plain English first'); return; }
    setNlBusy(true);
    try {
      const res = await authJsonFetch(`${API}/automation/rules/parse-nl`, {
        method: 'POST',
        body: JSON.stringify({ text: nlInput, year: editingRule?.year || new Date().getFullYear() }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'AI parse failed'); return; }
      const data = await res.json();
      setEditingRule(prev => ({ ...EMPTY_RULE, ...prev, ...data.draft, natural_language: nlInput }));
      setEditorOpen(true);
      toast.success('AI draft loaded — review the trigger + action before saving.');
    } finally {
      setNlBusy(false);
    }
  };

  const updateTrigger = (idx, patch) => {
    setEditingRule(prev => ({ ...prev, triggers: prev.triggers.map((t, i) => i === idx ? { ...t, ...patch } : t) }));
  };
  const addTrigger = () => setEditingRule(prev => ({ ...prev, triggers: [...(prev.triggers || []), { kind: 'field_value', field: '', operator: 'equals', value: '' }] }));
  const removeTrigger = (idx) => setEditingRule(prev => ({ ...prev, triggers: prev.triggers.filter((_, i) => i !== idx) }));

  const updateAction = (idx, patch) => setEditingRule(prev => ({ ...prev, actions: prev.actions.map((a, i) => i === idx ? { ...a, ...patch } : a) }));
  const addAction = () => setEditingRule(prev => ({ ...prev, actions: [...(prev.actions || []), { kind: 'send_email', recipient_type: 'primary_contact', subject: '', body: '' }] }));
  const removeAction = (idx) => setEditingRule(prev => ({ ...prev, actions: prev.actions.filter((_, i) => i !== idx) }));

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="automation-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Bot className="w-7 h-7" /> Automation Assistant</h1>
          <p className="text-muted-foreground mt-1">
            Build rules that trigger emails or data updates when (year, field, condition) matches. Every change is logged and can be undone.
          </p>
        </div>
        <Button onClick={openNew} data-testid="automation-new-rule-btn">
          <Plus className="w-4 h-4 mr-2" /> New Rule
        </Button>
      </div>

      {/* Natural-language quick-create */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4" /> Describe a rule in plain English</CardTitle>
          <CardDescription>
            e.g. "For 2026 schools where marketing_status is empty, send the Booking Reminder template to the primary contact"
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              value={nlInput}
              onChange={(e) => setNlInput(e.target.value)}
              placeholder="Type what you want to automate…"
              data-testid="automation-nl-input"
            />
            <Button onClick={parseNL} disabled={nlBusy || !nlInput.trim()}>
              {nlBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Draft with AI
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Rules list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Rules ({rules.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : rules.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No rules yet. Click <strong>New Rule</strong> or describe one above with AI.</p>
          ) : (
            <div className="space-y-2">
              {rules.map(rule => (
                <div key={rule.rule_id} className="p-4 border rounded-lg flex flex-wrap items-center gap-3" data-testid={`automation-rule-${rule.rule_id}`}>
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{rule.name}</span>
                      <Badge variant="outline">{rule.year}</Badge>
                      <Badge variant={rule.schedule === 'manual' ? 'secondary' : 'default'}>{rule.schedule}</Badge>
                      {!rule.enabled && <Badge variant="destructive">disabled</Badge>}
                    </div>
                    {rule.description && <p className="text-xs text-muted-foreground mt-1">{rule.description}</p>}
                    <p className="text-xs text-muted-foreground mt-1">
                      {(rule.triggers || []).length} trigger{(rule.triggers || []).length === 1 ? '' : 's'} · {(rule.actions || []).length} action{(rule.actions || []).length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <Switch checked={rule.enabled} onCheckedChange={() => toggleEnabled(rule)} data-testid={`automation-toggle-${rule.rule_id}`} />
                  <Button variant="outline" size="sm" onClick={() => runRule(rule, { dry_run: true })} title="Dry run — compute matches without applying">
                    <Play className="w-3.5 h-3.5 mr-1" /> Dry
                  </Button>
                  <Button size="sm" onClick={() => runRule(rule)} disabled={running[rule.rule_id]} data-testid={`automation-run-${rule.rule_id}`}>
                    {running[rule.rule_id] ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                    Run now
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(rule)} data-testid={`automation-edit-${rule.rule_id}`}><Edit3 className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => deleteRule(rule)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent runs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Recent Runs ({runs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No runs yet.</p>
          ) : (
            <div className="space-y-2">
              {runs.map(run => (
                <div key={run.run_id} className="p-3 border rounded-md flex items-center gap-3" data-testid={`automation-run-row-${run.run_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{run.rule_name || run.rule_id}</span>
                      <Badge variant="outline" className="text-xs">{run.user_email}</Badge>
                      {run.undone && <Badge variant="secondary">undone</Badge>}
                      {run.blocked && <Badge variant="destructive">blocked (loop)</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {new Date(run.started_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })} · matched {run.summary?.matched ?? 0} · applied {run.summary?.applied ?? 0} · queued {run.summary?.queued_email ?? 0} · failed {run.summary?.failed ?? 0}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setRunDetail(run)}><ChevronRight className="w-4 h-4" /></Button>
                  {!run.undone && !run.blocked && (run.summary?.applied || run.summary?.queued_email) ? (
                    <Button variant="outline" size="sm" onClick={() => undoRun(run)} data-testid={`automation-undo-${run.run_id}`}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Undo</Button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRule?.rule_id ? 'Edit Rule' : 'New Automation Rule'}</DialogTitle>
            <DialogDescription>
              Every change made by this rule is logged in Recent Runs and can be undone.
            </DialogDescription>
          </DialogHeader>
          {editingRule && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label>Name *</Label>
                  <Input value={editingRule.name} onChange={(e) => setEditingRule({ ...editingRule, name: e.target.value })} data-testid="automation-rule-name" />
                </div>
                <div>
                  <Label>Year</Label>
                  <Input type="number" value={editingRule.year} onChange={(e) => setEditingRule({ ...editingRule, year: parseInt(e.target.value, 10) || new Date().getFullYear() })} />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={editingRule.description || ''} onChange={(e) => setEditingRule({ ...editingRule, description: e.target.value })} />
              </div>

              <div className="p-3 border rounded-md space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Triggers</Label>
                  <Select value={editingRule.trigger_match} onValueChange={(v) => setEditingRule({ ...editingRule, trigger_match: v })}>
                    <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Match ALL (AND)</SelectItem>
                      <SelectItem value="any">Match ANY (OR)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(editingRule.triggers || []).map((t, idx) => {
                  const ops = t.kind === 'date_based' ? TRIGGER_OPERATORS_DATE : TRIGGER_OPERATORS_FIELD;
                  const noValueOps = ['is_empty', 'is_not_empty'];
                  return (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-2">
                        <Label className="text-xs">Kind</Label>
                        <Select value={t.kind} onValueChange={(v) => updateTrigger(idx, { kind: v, operator: v === 'date_based' ? 'days_since_update_gte' : 'equals' })}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="field_value">Field value</SelectItem>
                            <SelectItem value="date_based">Date-based</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Field</Label>
                        <Input value={t.field || ''} onChange={(e) => updateTrigger(idx, { field: e.target.value })} className="h-8 text-xs" placeholder="marketing_status" />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Operator</Label>
                        <Select value={t.operator || ''} onValueChange={(v) => updateTrigger(idx, { operator: v })}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{ops.map(op => <SelectItem key={op} value={op}>{op}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Value</Label>
                        <Input
                          disabled={noValueOps.includes(t.operator)}
                          value={t.value ?? ''}
                          onChange={(e) => updateTrigger(idx, { value: e.target.value })}
                          className="h-8 text-xs"
                        />
                      </div>
                      <Button variant="ghost" size="sm" className="col-span-1" onClick={() => removeTrigger(idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  );
                })}
                <Button variant="outline" size="sm" onClick={addTrigger}><Plus className="w-3.5 h-3.5 mr-1" /> Add trigger</Button>
              </div>

              <div className="p-3 border rounded-md space-y-3">
                <Label className="font-semibold">Actions</Label>
                {(editingRule.actions || []).map((a, idx) => (
                  <div key={idx} className="p-3 bg-muted/50 rounded space-y-2">
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-3">
                        <Label className="text-xs">Action</Label>
                        <Select value={a.kind} onValueChange={(v) => updateAction(idx, { kind: v })}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="send_email">Send Email (queued)</SelectItem>
                            <SelectItem value="update_field">Update Field</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {a.kind === 'send_email' ? (
                        <div className="col-span-8">
                          <Label className="text-xs">Recipient</Label>
                          <Select value={a.recipient_type || 'primary_contact'} onValueChange={(v) => updateAction(idx, { recipient_type: v })}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="primary_contact">Org's primary contact</SelectItem>
                              <SelectItem value="job_assigned_to">Whoever is in job_assigned_to</SelectItem>
                              <SelectItem value="fixed">Fixed email list</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <div className="col-span-8 grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Target field</Label>
                            <Input className="h-8 text-xs" value={a.target_field || ''} onChange={(e) => updateAction(idx, { target_field: e.target.value })} placeholder="marketing_status" />
                          </div>
                          <div>
                            <Label className="text-xs">New value</Label>
                            <Input className="h-8 text-xs" value={a.target_value ?? ''} onChange={(e) => updateAction(idx, { target_value: e.target.value })} />
                          </div>
                        </div>
                      )}
                      <Button variant="ghost" size="sm" className="col-span-1" onClick={() => removeAction(idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                    {a.kind === 'send_email' && (
                      <>
                        {a.recipient_type === 'fixed' && (
                          <div>
                            <Label className="text-xs">Fixed recipients (comma-separated)</Label>
                            <Input className="h-8 text-xs" value={(a.fixed_recipients || []).join(', ')} onChange={(e) => updateAction(idx, { fixed_recipients: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
                          </div>
                        )}
                        <div>
                          <Label className="text-xs">Subject</Label>
                          <Input className="h-8 text-xs" value={a.subject || ''} onChange={(e) => updateAction(idx, { subject: e.target.value })} placeholder="Reminder: {{org_name}}" />
                        </div>
                        <div>
                          <Label className="text-xs">Body</Label>
                          <Textarea rows={4} value={a.body || ''} onChange={(e) => updateAction(idx, { body: e.target.value })} placeholder="Dear {{contact_name}}, …" className="text-xs" />
                        </div>
                        <p className="text-[11px] text-muted-foreground"><MailIcon className="inline w-3 h-3 mr-1" /> Emails are QUEUED to the bulk-email scheduler (5-min delay). Review in Email Audit before they leave.</p>
                      </>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addAction}><Plus className="w-3.5 h-3.5 mr-1" /> Add action</Button>
              </div>

              <div className="flex items-center gap-4">
                <div>
                  <Label className="text-xs">Schedule</Label>
                  <Select value={editingRule.schedule} onValueChange={(v) => setEditingRule({ ...editingRule, schedule: v })}>
                    <SelectTrigger className="w-48 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual (run-now only)</SelectItem>
                      <SelectItem value="daily_8am_sydney">Daily 8am Sydney</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-5">
                  <Switch checked={editingRule.enabled} onCheckedChange={(v) => setEditingRule({ ...editingRule, enabled: v })} />
                  <Label className="text-sm">Enabled</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={saveRule} disabled={saving} data-testid="automation-save-rule-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {editingRule?.rule_id ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loop-guard confirm dialog */}
      <Dialog open={cascadeWarnings.open} onOpenChange={(o) => setCascadeWarnings({ ...cascadeWarnings, open: o })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Cascading rule run blocked</DialogTitle>
            <DialogDescription>
              This run would re-trigger other enabled rules on the same orgs. Confirm to proceed.
            </DialogDescription>
          </DialogHeader>
          <ul className="list-disc pl-5 text-sm space-y-1">
            {cascadeWarnings.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCascadeWarnings({ open: false, ruleId: null, warnings: [] })}>Cancel</Button>
            <Button onClick={async () => {
              const rule = rules.find(r => r.rule_id === cascadeWarnings.ruleId);
              setCascadeWarnings({ open: false, ruleId: null, warnings: [] });
              if (rule) await runRule(rule, { confirm_cascade: true });
            }}>
              <Play className="w-4 h-4 mr-2" /> Run with cascade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run-detail dialog */}
      <Dialog open={!!runDetail} onOpenChange={(o) => !o && setRunDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Run detail — {runDetail?.rule_name}</DialogTitle>
            <DialogDescription>
              {runDetail && new Date(runDetail.started_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })} · {runDetail?.user_email}
            </DialogDescription>
          </DialogHeader>
          {runDetail && (
            <div className="space-y-3 text-sm">
              <div className="flex gap-3 text-xs flex-wrap">
                <Badge variant="outline">matched {runDetail.summary?.matched ?? 0}</Badge>
                <Badge variant="outline">applied {runDetail.summary?.applied ?? 0}</Badge>
                <Badge variant="outline">queued {runDetail.summary?.queued_email ?? 0}</Badge>
                <Badge variant="outline">skipped {runDetail.summary?.skipped ?? 0}</Badge>
                <Badge variant="outline">failed {runDetail.summary?.failed ?? 0}</Badge>
                {runDetail.dry_run && <Badge variant="secondary">dry run</Badge>}
                {runDetail.undone && <Badge variant="secondary">undone</Badge>}
              </div>
              <div className="border rounded max-h-[55vh] overflow-y-auto p-2 space-y-1">
                {(runDetail.changes || []).map((c, i) => (
                  <div key={i} className="text-xs p-1.5 border-b last:border-b-0">
                    {c.status === 'applied' && <CheckCircle2 className="inline w-3 h-3 mr-1 text-emerald-600" />}
                    {c.status === 'failed' && <XCircle className="inline w-3 h-3 mr-1 text-red-600" />}
                    {c.status === 'queued_email' && <MailIcon className="inline w-3 h-3 mr-1 text-blue-600" />}
                    <strong>{c.status}</strong> · {c.org_name || c.org_id}
                    {c.field && <> · {c.field}: <code>{String(c.before ?? '∅')}</code> → <code>{String(c.after ?? '∅')}</code></>}
                    {c.recipients && <> · to {c.recipients.join(', ')}</>}
                    {c.error && <> · <span className="text-red-700">{c.error}</span></>}
                    {c.reason && <> · {c.reason}</>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
